import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription, catchError } from 'rxjs';
import { Store } from '@ngrx/store';

import { FormCompanyService } from '../../services/forms/form-company.service';
import { CompanyService } from 'src/app/services/company.service';
import { AuthService } from 'src/app/services/auth.service';
import { ContextService } from 'src/app/services/context.service';
import { ICompany } from 'src/app/models/company.model';
import { IError } from 'src/app/models/error.model';
import { setWarningText } from 'src/app/reducers/warning-popup.reducer';

interface Step {
  label: string;
  route: string;
  rootOnly?: boolean;
}

@Component({
  selector: 'app-company-form-v2',
  templateUrl: './company-form.component.html',
  styleUrls: ['./company-form.component.sass']
})
export class CompanyFormComponentV2 implements OnInit, OnDestroy {

  currentStep = 0;
  companyId: string | null = null;
  loading = false;
  isEditMode = false;
  isRoot = false;

  private allSteps: Step[] = [
    { label: 'Informações', route: 'info' },
    { label: 'Contato', route: 'contact', rootOnly: true },
    { label: 'Configurações', route: 'settings', rootOnly: true }
  ];

  steps: Step[] = [];
  
  private saveSubscription?: Subscription;
  private isSaving = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private formCompanyService: FormCompanyService,
    private companyService: CompanyService,
    private authService: AuthService,
    private contextService: ContextService,
    private store: Store<{ warningPopup: IError }>
  ) {
    const permission = this.authService.getPermission();
    this.isRoot = permission === 'ROOT';
  }

  ngOnInit(): void {
    // Detecta se é modo de edição pela URL
    this.isEditMode = this.router.url.includes('/edit');
    this.companyId = this.route.snapshot.paramMap.get('id');

    // IMPORTANTE: Limpa dados anteriores antes de iniciar um novo formulário
    // Isso evita que templates/módulos de uma empresa sejam aplicados em outra
    this.formCompanyService.clear();

    // Configura o modo no serviço
    this.formCompanyService.setMode(this.isEditMode ? 'edit' : 'create');
    
    // Configura o ID da empresa se for edição
    if (this.companyId) {
      this.formCompanyService.setCompanyId(this.companyId);
    }

    // Filtra steps: ROOT puro (sem clínica ativa) vê tudo;
    // ROOT se passando por ADM (com clínica ativa) ou ADM puro não vê tabs rootOnly
    const isRootActingAsAdmin = this.isRoot && !!this.contextService.getActiveClinicSnapshot()?.cnpj;
    if (this.isEditMode && (!this.isRoot || isRootActingAsAdmin)) {
      this.steps = this.allSteps.filter(step => !step.rootOnly);
    } else {
      this.steps = this.allSteps;
    }

    // Expõe as rotas permitidas para os steps através do serviço
    this.formCompanyService.setAllowedRoutes(this.steps.map(s => s.route));

    // Se for edição, carrega os dados da empresa
    if (this.isEditMode && this.companyId) {
      this.loadCompanyData(this.companyId);
    }

    this.syncStepFromRoute();

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.syncStepFromRoute());
    
    // Escuta o evento de save do header (apenas no modo edit)
    if (this.isEditMode) {
      this.saveSubscription = this.formCompanyService.onSaveRequest().subscribe(() => {
        this.saveCompany();
      });
    }
  }

  ngOnDestroy(): void {
    // Limpa os dados do serviço ao sair do componente
    // (apenas se navegar para fora do fluxo de empresa)
    // Não limpar aqui pois os steps precisam dos dados
    this.saveSubscription?.unsubscribe();
  }

  private loadCompanyData(id: string): void {
    this.loading = true;
    this.formCompanyService.setCompanyId(id);
    this.companyService.getCompanyById(id, true)
      .then((company: ICompany) => {
        this.formCompanyService.setCompanyData(company);
        this.loading = false;
      })
      .catch((error: any) => {
        console.error('Erro ao carregar empresa:', error);
        this.loading = false;
      });
  }

  goToStep(stepIndex: number): void {
    const step = this.steps[stepIndex];
    if (!step) return;

    this.currentStep = stepIndex;

    this.router.navigate([step.route], {
      relativeTo: this.route
    });
  }

  private syncStepFromRoute(): void {
    const childRoute = this.route.firstChild?.snapshot;
    if (!childRoute) return;

    // Tenta pegar do data primeiro
    const stepIndex = childRoute.data['stepIndex'];
    if (typeof stepIndex === 'number') {
      this.currentStep = stepIndex;
      return;
    }

    // Fallback: encontra pelo path da rota
    const currentRoutePath = childRoute.routeConfig?.path;
    if (currentRoutePath) {
      const foundIndex = this.steps.findIndex(s => s.route === currentRoutePath);
      if (foundIndex !== -1) {
        this.currentStep = foundIndex;
      }
    }
  }

  private saveCompany(): void {
    if (this.isSaving) return;
    this.isSaving = true;
    this.loading = true;

    // Aguarda um tick para garantir que todos os subscribers do saveRequest
    // (como o settings.component) já salvaram seus dados no serviço
    setTimeout(() => this.performSave(), 0);
  }

  private performSave(): void {
    // Monta o payload da empresa com todos os dados dos steps
    const allData = this.formCompanyService.getAll();
    const info = allData.info;
    const address = allData.address;
    const contact = allData.contact;
    
    // Pega módulos e templates do serviço
    const modules = this.formCompanyService.getModules();
    const templates = this.formCompanyService.getTemplates();
    // Se não houver templates selecionados no serviço, usa os originais da empresa
    const activeTemplates = templates.length > 0 ? templates : this.formCompanyService.getCompanyTemplates();

    const cnpj = info?.cnpj?.replace(/[^\d]+/g, '') || '';

    // Verifica se há uma nova imagem (File) que precisa ser enviada ao S3
    const imageValue = info?.image;
    const isNewFile = imageValue instanceof File;

    const buildAndSaveCompany = (logoValue: string) => {
      const companyRequest: ICompany = {
        cnpj,
        corporate_name: info?.corporateName?.trim() || '',
        logo: logoValue,
        logo_position: info?.imagePosition === 'watermark' ? 'WATERMARK' : 'HEADERS',
        is_blocked: info?.status === 'false',
        contracted_anamnese_type: info?.contractedAnamneseType || 'NOT_CONTRACTED',
        use_default_anamnese: info?.useDefaultAnamnese ?? true,
        is_model_patient_active: info?.pacienteModelo === 'ativo',
        legal_email: contact?.legalEmail?.trim().toLowerCase() || '',
        address: {
          street: address?.street?.trim() || '',
          number: address?.number?.trim() || '',
          complement: address?.complement?.trim() || 'Sem complemento',
          neighborhood: address?.neighborhood?.trim() || '',
          city: address?.city?.trim() || '',
          state: address?.state?.trim() || '',
          zip_code: address?.cep?.trim() || ''
        },
        documents: activeTemplates,
        witness_one_name: info?.firstWitnessName?.trim() || '',
        witness_one_document: info?.firstWitnessDocument?.trim() || '',
        witness_two_name: info?.secondWitnessName?.trim() || '',
        witness_two_document: info?.secondWitnessDocument?.trim() || '',
        sign_delivery_type: 'DELIVERY_METHOD_SMS',
        modules: modules,
        has_treatment_plans: info?.funcionalidadesBeta?.planoTratamento || false,
        has_calculator: info?.funcionalidadesBeta?.calculadora || false,
        group_id: info?.groupId || undefined
      };

      const save$ = this.isEditMode
        ? this.companyService.updateCompany(this.formCompanyService.getCompanyId()!, companyRequest)
        : this.companyService.createCompany(companyRequest);

      save$
        .pipe(
          catchError(error => {
            this.isSaving = false;
            this.loading = false;
            this.store.dispatch(setWarningText(this.isEditMode ? "Erro ao atualizar empresa." : "Erro ao criar empresa."));
            throw error;
          })
        )
        .subscribe((updatedCompany) => {
          this.isSaving = false;
          this.loading = false;
          if (updatedCompany) {
            this.formCompanyService.setCompanyData(updatedCompany);
          }
          this.store.dispatch(setWarningText(this.isEditMode ? "Empresa atualizada com sucesso!" : "Empresa criada com sucesso!"));
          if (!this.isEditMode) {
            this.router.navigate(['/main/v2/company']);
          }
        });
    };

    if (isNewFile) {
      // Upload da logo para o S3 via presigned URL
      const file = imageValue as File;
      this.companyService.generateLogoUploadUrl(cnpj, file.name, file.type)
        .then(response => {
          return this.companyService.uploadFileToS3(response.uploadUrl, file)
            .then(() => response.objectKey);
        })
        .then(objectKey => {
          buildAndSaveCompany(objectKey);
        })
        .catch(() => {
          this.isSaving = false;
          this.loading = false;
          this.store.dispatch(setWarningText("Erro ao enviar logo. Tente novamente."));
        });
    } else {
      // Logo não mudou (string com S3 key/URL) ou foi removida ('')
      const logoValue = typeof imageValue === 'string' ? imageValue : '';
      buildAndSaveCompany(logoValue);
    }
  }
}
